import React, {type ReactElement} from "react";

import {css} from "/src/utils/utils";
import CTAButton from "../buttons/cta-button/CTAButton";
import ModuleBody from "../layouts/module-body/ModuleBody";
import TransitionBlock, {
  TRANSITION_BLOCK_FROM,
} from "../animation/transition-block/TransitionBlock";

import * as styles from "./careers-three-column-list.module.scss";

interface Props {
  title?: string;
  copy?: string;
  items?: Array<{
    title?: string;
    gifUrl?: string;
    copy?: string;
    cta?: {
      label: string;
      url: string;
      amplitudeCtaName?: string;
      amplitudeCtaType?: string;
    };
  }>;
  anchorLinkId?: string | null;
}

export default function CareersThreeColumnList(props: Props): ReactElement {
  const {copy, title, items, anchorLinkId} = props;

  return (
    <section {...(anchorLinkId ? {id: anchorLinkId} : {})}>
      <ModuleBody classes={styles.careers}>
        {(title || copy) && (
          <TransitionBlock
            threshold={1}
            triggerOnce
            from={TRANSITION_BLOCK_FROM.bottom}
            transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
          >
            {title && (
              <h2 className={css(styles.careers__title, "h1")}>{title}</h2>
            )}

            {copy && (
              <p className={css(styles.careers__subtitle, "h3")}>{copy}</p>
            )}
          </TransitionBlock>
        )}

        <ul className={styles.careers__items}>
          {items &&
            items.map((item, i) => {
              const key = item ? `${item.title}-${i}` : `item-${i}`;

              return item.title ? (
                <li className={styles.careers__item} key={key}>
                  <TransitionBlock
                    delay={50}
                    from={TRANSITION_BLOCK_FROM.bottom}
                    threshold={1}
                    transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
                    triggerOnce
                  >
                    {item.gifUrl && (
                      <div className={css(styles.careers__item__imageWrapper)}>
                        <img
                          alt="interactive careers gif"
                          className={styles.careers__item__image}
                          src={item.gifUrl}
                        />
                      </div>
                    )}

                    {item.title && (
                      <h3 className={css(styles.careers__item__title, "h3")}>
                        {item.title}
                      </h3>
                    )}

                    {item.copy && (
                      <p className={css(styles.careers__item__copy, "p")}>
                        {item.copy}
                      </p>
                    )}

                    {item.cta && (
                      <CTAButton
                        classes={styles.careers__item__cta}
                        cta={item.cta}
                        kind="primary"
                      />
                    )}
                  </TransitionBlock>
                </li>
              ) : null;
            })}
        </ul>
      </ModuleBody>
    </section>
  );
}
